import {
  Button,
  Divider,
  Group,
  Select,
  SelectProps,
  Stack,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { size } from 'lodash/fp';
import React, { useState } from 'react';

import { DeviceModelType } from '@portals/api/organizations';
import { ModalFooter } from '@portals/core';
import { StateType } from '@portals/types';

import { TroubleshootAndSupportMessage } from './TroubleshootAndSupportMessage';
import { useCloud2CloudFormData } from '../../add-device-modal.hooks';
import { FormComponentCommonProps } from '../../add-device-modal.types';
import { ConfigurationMissingMessage } from '../../ConfigurationMissingMessage';
import { ModelField } from '../fields/ModelField';
import { VendorField } from '../fields/VendorField';

export function ZoomForm({
  selectedPartnerId,
  setSelectedPartnerId,
  handleSubmit,
  handleClose,
  isSubmitDisabled,
  partnerModels,
}: FormComponentCommonProps) {
  const [selectedDeviceModel, setSelectedDeviceModel] =
    useState<DeviceModelType | null>(null);

  const {
    integration,
    fetchIntegration,
    isLoading,
    options,
    showConfigurationMissingMessage,
  } = useCloud2CloudFormData({
    integrationName: 'zoom',
    composeOptions: composeZoomRoomOptions,
  });

  const form = useForm({
    initialValues: {
      zoom_room: '',
      name: '',
      model: '',
    },
  });

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack p="xxl">
        <VendorField
          vendor={selectedPartnerId}
          setVendor={setSelectedPartnerId}
        />

        {showConfigurationMissingMessage ? (
          <ConfigurationMissingMessage
            integration={integration}
            message="Please configure the Zoom integration before claiming devices."
            linkTo="/settings/integrations/uc/zoom"
            onLinkClick={handleClose}
          />
        ) : (
          <Stack>
            {/* @todo: validate if it's needed to have a model field at all */}
            <ModelField
              partnerId={selectedPartnerId}
              fieldName="model"
              value={form.values.model}
              setFieldValue={(name, value) => {
                form.setFieldValue(name, value);

                setSelectedDeviceModel(
                  partnerModels.find((model) => model.id === value) || null
                );
              }}
            />

            <Group noWrap align="end">
              {size(options) === 0 ? (
                <span>No rooms found</span>
              ) : (
                <Select
                  data={options}
                  disabled={isLoading}
                  label="Zoom room"
                  placeholder="Select room"
                />
              )}

              <Button
                loading={isLoading}
                onClick={() => fetchIntegration({ type: 'zoom' })}
              >
                Refresh
              </Button>
            </Group>

            <TextInput
              label="Name"
              placeholder="Name"
              {...form.getInputProps('name')}
            />
          </Stack>
        )}
        {selectedPartnerId && (
          <TroubleshootAndSupportMessage
            handleClose={handleClose}
            modelInfo={selectedDeviceModel}
          />
        )}
      </Stack>

      <Divider color="gray.2" />

      <ModalFooter position="right" p="xxl">
        <Button variant="default" onClick={handleClose}>
          Cancel
        </Button>

        <Button
          type="submit"
          disabled={
            isSubmitDisabled || !form.values.zoom_room || !form.values.model
          }
          loading={isLoading}
        >
          Claim device
        </Button>
      </ModalFooter>
    </form>
  );
}

function composeZoomRoomOptions(
  integration: StateType['data']['integrations']['zoom']
): SelectProps['data'] {
  const unclaimed = integration?.extra?.unclaimed;

  if (!unclaimed) {
    return [];
  }

  return unclaimed.map((room) => ({
    value: room.id,
    label: room.name,
  }));
}
